/*
First-party ad attribution.

Captures utm_* / ad click ids / referrer at page load and keeps two records in
raw localStorage (deliberately outside redux-persist — the persisted blob is
versioned and migrated in store.js, and attribution must survive those resets):

  - firstTouch: the visitor's very first landing. Written once, never
    overwritten — this is the record that answers "which ad brought them".
  - lastTouch: refreshed whenever a later visit carries campaign params or
    arrives from an external referrer.

captureAttribution() MUST run at index.js module scope, before React mounts:
RedirectHandler and UseCasesPage rewrite the query string after first render,
so anything captured later can miss the ad params.

Every storage/URL access is best-effort and swallows its own errors (private
mode, quota) — attribution must never break the app.
*/

import { PLAN_UNKNOWN } from 'functions/entitlements/featureEntitlements';

const FIRST_TOUCH_KEY = "attribution.firstTouch";
const LAST_TOUCH_KEY = "attribution.lastTouch";
const SIGNUP_TRACKED_KEY = "attribution.signupTracked";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
// gbraid/wbraid are Google's own newer click ids (app and web-to-app campaigns)
// and match no click-id naming convention at all — a reminder that the shape
// check below is a safety net, not a substitute for naming the platforms we use.
const CLICK_ID_KEYS = [
  "gclid", "gbraid", "wbraid", // Google
  "msclkid",                   // Microsoft
  "fbclid",                    // Meta
  "ttclid",                    // TikTok
  "twclid",                    // X
  "li_fat_id",                 // LinkedIn
  "epik",                      // Pinterest
  "sccid",                     // Snapchat
  "qclid",                     // Quora
];

/*
Click ids we have never heard of.

A click id exists for exactly one purpose: to hand back to the ad platform later
and say "this person converted". It is only ever present in the URL of the
landing hit — miss it there and it cannot be recovered, because by the time
someone signs up the parameter is long gone from their address bar. That makes an
allowlist the wrong shape: it works for the platforms we already advertise on and
silently drops the first click from any new one, which is precisely when the
attribution matters most.

So anything that LOOKS like a click id is kept too. Deliberately narrow, because
this is a URL a stranger controls and we are writing it to storage that later
travels to Clerk and to analytics:

  - the name has to match a click-id shape (…clid, …click_id, …_cid)
  - the value has to be an opaque token — no spaces, no "@", bounded length
  - at most MAX_UNKNOWN_CLICK_IDS of them, so a crafted URL cannot fill storage

Anything failing those is ignored rather than stored. We are not trying to
capture arbitrary query strings: a page hit with ?email=someone@example.com must
never end up in an analytics property or a Clerk metadata field.
*/
// The middle is {0,31}, not {1,31}: requiring two characters before the suffix
// silently missed short real names like `qclid`. Found by running the pattern
// against a list of actual platform click ids rather than reasoning about it.
const UNKNOWN_CLICK_ID_PATTERN = /^[a-z][a-z0-9_]{0,31}(clid|click_?id|_cid)$/;
const MAX_UNKNOWN_CLICK_IDS = 4;
const MAX_CLICK_ID_LENGTH = 512;

/** Does this query parameter look like an ad platform's click id we don't know yet? */
export function isProbableClickId(key, value) {
  if (typeof key !== "string" || typeof value !== "string") return false;
  if (!UNKNOWN_CLICK_ID_PATTERN.test(key.toLowerCase())) return false;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > MAX_CLICK_ID_LENGTH) return false;
  // An opaque token, not a sentence and not a person.
  if (/\s/.test(trimmed) || trimmed.includes("@")) return false;
  return true;
}

function readJson(key) {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

function writeJson(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    /* ignore storage failures (e.g. private mode) */
  }
}

function isExternalReferrer(referrer, host) {
  if (!referrer) return false;
  try {
    return new URL(referrer).hostname !== host;
  } catch (e) {
    return false;
  }
}

/**
 * Record the current visit's attribution. Call once, at module scope in
 * index.js, before React mounts. `overrides` exists for tests.
 */
export function captureAttribution(overrides = {}) {
  try {
    const search = overrides.search !== undefined ? overrides.search : window.location.search;
    const path = overrides.path !== undefined ? overrides.path : window.location.pathname;
    const referrer = overrides.referrer !== undefined ? overrides.referrer : document.referrer;
    const host = overrides.host !== undefined ? overrides.host : window.location.hostname;

    const params = new URLSearchParams(search || "");
    const touch = {};
    let hasCampaignParams = false;
    [...UTM_KEYS, ...CLICK_ID_KEYS].forEach((key) => {
      const value = params.get(key);
      if (value) {
        touch[key] = value;
        hasCampaignParams = true;
      }
    });

    // A click id from a platform we have not advertised on before. Kept so the
    // FIRST campaign on a new network is attributable, instead of the one after
    // we notice and ship a fix.
    let unknownKept = 0;
    for (const [key, value] of params.entries()) {
      if (unknownKept >= MAX_UNKNOWN_CLICK_IDS) break;
      const lower = String(key).toLowerCase();
      if (touch[lower] !== undefined) continue;
      if (UTM_KEYS.includes(lower) || CLICK_ID_KEYS.includes(lower)) continue;
      if (!isProbableClickId(lower, value)) continue;
      touch[lower] = value.trim();
      hasCampaignParams = true;
      unknownKept += 1;
    }
    if (referrer) touch.referrer = referrer;
    touch.landing_path = path;
    touch.ts = new Date().toISOString();

    // First touch is write-once — even an organic landing is worth recording
    // as a baseline (referrer + landing page), so later sign-ups aren't blank.
    if (!readJson(FIRST_TOUCH_KEY)) writeJson(FIRST_TOUCH_KEY, touch);

    // Last touch only refreshes on a visit that carries campaign identity;
    // ordinary in-product reloads must not clobber it.
    if (hasCampaignParams || isExternalReferrer(referrer, host)) {
      writeJson(LAST_TOUCH_KEY, touch);
    }
  } catch (e) {
    /* attribution must never break boot */
  }
}

export function getFirstTouch() {
  return readJson(FIRST_TOUCH_KEY);
}

export function getLastTouch() {
  return readJson(LAST_TOUCH_KEY);
}

/**
 * Flat `ft_*` / `lt_*` props for event enrichment — safe to spread into any
 * analytics call. Returns {} when nothing has been captured.
 */
export function getAttributionProps() {
  const props = {};
  const flatten = (touch, prefix) => {
    if (!touch) return;
    Object.keys(touch).forEach((key) => {
      if (key !== "ts" && touch[key] != null) props[prefix + key] = touch[key];
    });
  };
  flatten(getFirstTouch(), "ft_");
  flatten(getLastTouch(), "lt_");
  return props;
}

/**
 * Compact human-readable first-touch summary for free-text fields (e.g. the
 * /subscribe `source` column), like "ft:google/cpc/brand gclid". Returns ""
 * when nothing campaign-shaped was captured.
 */
export function getAttributionSourceSuffix() {
  const first = getFirstTouch();
  if (!first) return "";
  const parts = [];
  const trio = [first.utm_source, first.utm_medium, first.utm_campaign].filter(Boolean).join("/");
  if (trio) parts.push("ft:" + trio);
  CLICK_ID_KEYS.forEach((key) => {
    if (first[key]) parts.push(key);
  });
  return parts.join(" ");
}

/**
 * Last plan we saw for this user, persisted so a free→paid transition is
 * detectable across sessions (the upgrade itself happens inside Clerk's
 * checkout, which the app cannot observe directly).
 */
const LAST_PLAN_KEY = "attribution.lastKnownPlan";

export function getLastKnownPlan(userId) {
  const plans = readJson(LAST_PLAN_KEY);
  return (plans && userId && plans[userId]) || null;
}

export function setLastKnownPlan(userId, plan) {
  if (!userId || !plan) return;
  // PLAN_UNKNOWN means the lookup failed, which is not an observation of this
  // user's plan. Recording it would overwrite the last real plan we saw and
  // permanently suppress their upgrade event, since `previous` would no longer
  // be "free". Rejected here as well as at the call site so the invariant
  // survives an edit to either one.
  if (plan === PLAN_UNKNOWN) return;
  const plans = readJson(LAST_PLAN_KEY) || {};
  plans[userId] = plan;
  writeJson(LAST_PLAN_KEY, plans);
}

/**
 * Whether a plan reading represents a free→paid upgrade worth reporting.
 *
 * Extracted from LoginSync so the rule is testable: the upgrade proxy compares
 * two values that can each be absent for different reasons, and getting it wrong
 * is invisible — it either invents revenue conversions or silently loses them.
 *
 * @param {object} params
 * @param {string|null} params.previousPlan - Last plan seen, or null if none.
 * @param {string} params.plan - The plan just read.
 * @param {boolean} params.resolved - Whether the backend actually answered.
 * @returns {boolean}
 */
export function isPlanUpgrade({ previousPlan, plan, resolved }) {
  // An unresolved lookup satisfies `plan !== "free"` purely because it is
  // PLAN_UNKNOWN, which would fire a conversion for a user who did not buy
  // anything.
  if (!resolved || plan === PLAN_UNKNOWN) return false;
  // A null previous plan is an existing user on a fresh browser, not an upgrade.
  return previousPlan === 'free' && plan !== 'free';
}

/**
 * Once-ever sign-up guard, persisted so multi-tab / reload / Clerk user-object
 * churn within the new-user window can't double-fire the conversion.
 */
export function hasTrackedSignup(userId) {
  const tracked = readJson(SIGNUP_TRACKED_KEY);
  return !!(tracked && userId && tracked[userId]);
}

export function markSignupTracked(userId) {
  if (!userId) return;
  const tracked = readJson(SIGNUP_TRACKED_KEY) || {};
  tracked[userId] = new Date().toISOString();
  writeJson(SIGNUP_TRACKED_KEY, tracked);
}
